import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { formatCurrency, formatDate } from '../shared/constants';
import { useExpenses } from './hooks/useExpenses';
import { useExpenseTable } from './hooks/useExpenseTable';
import MonthSelector from './MonthSelector';
import CategoryBreakdown from './CategoryBreakdown';
import ExpenseForm from './ExpenseForm';
import ExpenseFilters from './ExpenseFilters';
import Button from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import Table from '../../../components/ui/table';
import { ArrowUpDown, Trash2, Plus, Undo } from 'lucide-react';
import SublyApi from '../../../helpers/Api';

const EditableCell = ({ value, onSave, type = 'text', options = null, field = '' }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editValue, setEditValue] = useState(value);
	const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
	const cellRef = useRef(null);
	const popoverRef = useRef(null);

	useEffect(() => {
		setEditValue(value);
	}, [value]);

	// Determine if this field should use popover
	const usesPopover = type === 'text' && (field === 'name' || field === 'notes');
	const isLongText = value && value.toString().length > 40;

	// Click outside handler for popover
	useEffect(() => {
		if (isEditing && usesPopover) {
			const handleClickOutside = (event) => {
				if (popoverRef.current && !popoverRef.current.contains(event.target)) {
					handleSave();
				}
			};

			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [isEditing, usesPopover, editValue]);

	const handleSave = () => {
		setIsEditing(false);
		if (editValue !== value) {
			onSave(editValue);
		}
	};

	const handleCancel = () => {
		setEditValue(value);
		setIsEditing(false);
	};

	const handleBlur = () => {
		if (!usesPopover) {
			handleSave();
		}
	};

	const handleKeyDown = (e) => {
		// For name field and regular inputs: Enter saves
		if (e.key === 'Enter' && (field !== 'notes' || !usesPopover)) {
			e.preventDefault();
			handleSave();
		} else if (e.key === 'Escape') {
			handleCancel();
		} else if (e.key === 'Tab' && !usesPopover) {
			handleBlur();
		}
	};

	const handleEdit = () => {
		if (usesPopover && cellRef.current) {
			// Calculate popover position - use fixed positioning relative to viewport
			const rect = cellRef.current.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const viewportWidth = window.innerWidth;
			const popoverHeight = field === 'notes' ? 200 : 150;
			const isMobile = viewportWidth < 640; // sm breakpoint
			const popoverWidth = isMobile ? viewportWidth - 20 : 500;

			// Calculate available space in viewport
			const spaceBelow = viewportHeight - rect.bottom;
			const spaceAbove = rect.top;

			// Determine vertical position (viewport coordinates for fixed positioning)
			let top;
			let shouldPositionAbove = false;

			if (spaceBelow >= popoverHeight) {
				// Position below
				top = rect.bottom + 5;
			} else if (spaceAbove >= popoverHeight) {
				// Position above
				top = rect.top - popoverHeight - 10;
				shouldPositionAbove = true;
			} else {
				// Center in viewport
				top = (viewportHeight - popoverHeight) / 2;
			}

			// Determine horizontal position
			let left;
			if (isMobile) {
				// Center horizontally on mobile
				left = 10;
			} else {
				left = rect.left;
				// Ensure popover doesn't go off-screen
				if (left + popoverWidth > viewportWidth - 10) {
					left = viewportWidth - popoverWidth - 10;
				}
				left = Math.max(10, left);
			}

			setPopoverPosition({
				top,
				left,
				positionAbove: shouldPositionAbove,
			});
		}
		setIsEditing(true);
	};

	if (!isEditing) {
		let displayValue = value || '-';
		if (type === 'currency' && value) {
			displayValue = formatCurrency(value);
		} else if (type === 'date' && value) {
			displayValue = formatDate(value);
		}

		return (
			<div
				ref={cellRef}
				onClick={handleEdit}
				className='cursor-pointer hover:bg-gray-100 px-2 py-1 rounded min-h-[32px] flex items-center group'
				title={isLongText ? value : ''}>
				<span className='truncate block'>{displayValue}</span>
				{isLongText && (
					<span className='text-xs text-gray-400 ml-1 opacity-0 group-hover:opacity-100 transition-opacity'>
						({value.length})
					</span>
				)}
			</div>
		);
	}

	if (type === 'select' && options) {
		return (
			<select
				value={editValue}
				onChange={(e) => setEditValue(e.target.value)}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				autoFocus
				className='w-full px-2 py-1 border border-primary rounded focus:outline-none font-montserrat'>
				{options.map((option) => (
					<option key={option} value={option}>
						{option}
					</option>
				))}
			</select>
		);
	}

	if (type === 'currency') {
		return (
			<input
				type='number'
				step='0.01'
				value={editValue}
				onChange={(e) => setEditValue(e.target.value)}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				autoFocus
				className='w-full px-2 py-1 border border-primary rounded focus:outline-none font-montserrat'
			/>
		);
	}

	if (type === 'date') {
		return (
			<input
				type='date'
				value={editValue}
				onChange={(e) => setEditValue(e.target.value)}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				autoFocus
				className='w-full px-2 py-1 border border-primary rounded focus:outline-none font-montserrat'
			/>
		);
	}

	// Popover editor for long text fields (name and notes)
	if (usesPopover) {
		return createPortal(
			<div
				ref={popoverRef}
				className={`fixed z-50 bg-white shadow-2xl border-2 border-primary rounded-lg p-4 w-[calc(100vw-20px)] sm:w-auto ${
					popoverPosition.positionAbove ? 'transform -translate-y-full' : ''
				}`}
				style={{
					top: `${popoverPosition.top}px`,
					left: `${popoverPosition.left}px`,
					minWidth: field === 'notes' ? '400px' : 'auto',
					maxWidth: field === 'notes' ? '450px' : '600px',
				}}>
				<textarea
					value={editValue}
					onChange={(e) => setEditValue(e.target.value)}
					onKeyDown={handleKeyDown}
					className='w-full p-2 border border-gray-300 rounded resize-none font-montserrat focus:outline-none focus:ring-2 focus:ring-primary'
					rows={field === 'notes' ? 3 : 2}
					autoFocus
					onFocus={(e) => {
						// Move cursor to end of text
						const length = e.target.value.length;
						e.target.setSelectionRange(length, length);
					}}
				/>
				<div className='flex justify-end gap-2 mt-3'>
					<Button onClick={handleCancel} variant='outline' className='font-montserrat'>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						className='bg-primary hover:bg-primary/90 text-white font-montserrat'>
						Save
					</Button>
				</div>
			</div>,
			document.body
		);
	}

	// Regular inline input for other fields
	return (
		<input
			type='text'
			value={editValue}
			onChange={(e) => setEditValue(e.target.value)}
			onBlur={handleBlur}
			onKeyDown={handleKeyDown}
			autoFocus
			className='w-full px-2 py-1 border border-primary rounded focus:outline-none font-montserrat'
		/>
	);
};

const ClientExpenseTable = () => {
	const { clientId: username } = useParams(); // URL param is now username
	const history = useHistory();

	// Get current month
	const currentMonth = new Date().toISOString().slice(0, 7);
	const [selectedMonth, setSelectedMonth] = useState(currentMonth);
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [editingExpense, setEditingExpense] = useState(null);
	const [showFilters, setShowFilters] = useState(false);
	const [client, setClient] = useState(null);

	// Get expenses with API integration
	const {
		expenses: allExpenses,
		loading,
		error,
		fetchExpenses,
		addExpense,
		updateExpense,
		deleteExpense,
		bulkDeleteExpenses,
		undo,
		canUndo,
		getExpensesByMonth,
	} = useExpenses(username);

	// Fetch expenses when month changes and set client info
	useEffect(() => {
		const fetchData = async () => {
			// Calculate month date range
			const startDate = `${selectedMonth}-01`;
			// Get the last day of the month by going to next month's first day and subtracting 1 day
			const year = parseInt(selectedMonth.split('-')[0]);
			const month = parseInt(selectedMonth.split('-')[1]);
			const lastDay = new Date(year, month, 0).getDate();
			const endDate = `${selectedMonth}-${String(lastDay).padStart(2, '0')}`;

			// Fetch expenses for the selected month
			await fetchExpenses({
				start_date: startDate,
				end_date: endDate,
			});
		};

		if (username) {
			// Set basic client info immediately (will be updated with full info if expenses exist)
			setClient({
				username: username,
				name: username.charAt(0).toUpperCase() + username.slice(1),
				email: '',
			});

			fetchData();
		}
	}, [username, selectedMonth]);

	// Update client info from expense data when available
	useEffect(() => {
		if (allExpenses.length > 0 && allExpenses[0].firstName) {
			setClient({
				username: username,
				name: `${allExpenses[0].firstName} ${allExpenses[0].lastName}`,
				email: allExpenses[0].email,
			});
		}
	}, [allExpenses, username]);

	// Get expenses for selected month using helper
	const monthExpenses = getExpensesByMonth(selectedMonth);

	// Table state
	const {
		expenses,
		allExpenses: filteredExpenses,
		total,
		sortConfig,
		handleSort,
		filters,
		updateFilters,
		selectedIds,
		toggleSelection,
		toggleSelectAll,
		clearSelection,
		isAllSelected,
		currentPage,
		totalPages,
		setCurrentPage,
	} = useExpenseTable(monthExpenses);

	if (!client) {
		return (
			<div className='min-h-screen bg-gray-50 p-6 flex items-center justify-center'>
				<p className='text-gray-600 font-montserrat'>Client not found</p>
			</div>
		);
	}

	const handleAddExpense = () => {
		setEditingExpense(null);
		setIsFormOpen(true);
	};

	const handleFormSubmit = (expenseData) => {
		if (editingExpense) {
			updateExpense(editingExpense.id, expenseData);
		} else {
			addExpense(expenseData);
		}
	};

	const handleDeleteSelected = () => {
		if (
			window.confirm(
				`Are you sure you want to delete ${selectedIds.length} expense(s)?`
			)
		) {
			bulkDeleteExpenses(selectedIds);
			clearSelection();
		}
	};

	const handleUpdateField = (id, field, value) => {
		const updates = { [field]: value };
		if (field === 'amount') {
			updates.amount = parseFloat(value);
		}
		updateExpense(id, updates);
	};

	const getSortIcon = (key) => {
		if (sortConfig.key === key) {
			return sortConfig.direction === 'asc' ? '↑' : '↓';
		}
		return '⇅';
	};

	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='max-w-7xl mx-auto'>
				{/* Header */}
				<div className='mb-6'>
					<Button
						onClick={() => history.push('/admin/spending-tracker')}
						variant='outline'
						className='mb-4 font-montserrat'>
						← Back to Clients
					</Button>
					<div className='flex items-center justify-between'>
						<div>
							<h1 className='text-4xl font-glacial font-bold text-clientBlack mb-2'>
								{client.name}
							</h1>
							<p className='text-gray-600 font-montserrat'>
								Marketing Expenses
							</p>
						</div>
						<div className='flex items-center gap-4'>
							<MonthSelector
								value={selectedMonth}
								onChange={setSelectedMonth}
							/>
							<Button
								onClick={handleAddExpense}
								className='bg-primary hover:bg-primary/90 text-white font-montserrat'>
								<Plus className='w-4 h-4 mr-2' />
								Add Expense
							</Button>
						</div>
					</div>
				</div>

				{/* Category Breakdown */}
				<div className='mb-6'>
					<CategoryBreakdown expenses={filteredExpenses} />
				</div>

				{/* Toolbar */}
				<div className='bg-white rounded-lg border border-gray-200 p-4 mb-4'>
					<div className='flex items-center justify-between'>
						<div className='flex items-center gap-3'>
							{selectedIds.length > 0 && (
								<>
									<span className='text-sm font-montserrat text-gray-600'>
										{selectedIds.length} selected
									</span>
									<Button
										onClick={handleDeleteSelected}
										variant='destructive'
										size='sm'
										className='font-montserrat'>
										<Trash2 className='w-4 h-4 mr-2' />
										Delete Selected
									</Button>
								</>
							)}
							{canUndo && (
								<Button
									onClick={undo}
									variant='outline'
									size='sm'
									className='font-montserrat'>
									<Undo className='w-4 h-4 mr-2' />
									Undo
								</Button>
							)}
						</div>
						<div className='flex items-center gap-3'>
							<Button
								onClick={() => setShowFilters(!showFilters)}
								variant='outline'
								size='sm'
								className='font-montserrat'>
								{showFilters ? 'Hide' : 'Show'} Filters
							</Button>
							<Button variant='outline' size='sm' className='font-montserrat'>
								Export CSV
							</Button>
						</div>
					</div>

					{/* Filters */}
					{showFilters && (
						<div className='mt-4 pt-4 border-t border-gray-200'>
							<ExpenseFilters filters={filters} onUpdate={updateFilters} />
						</div>
					)}
				</div>

				{/* Expense Table */}
				<div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
					<div className='overflow-x-auto'>
					<Table>
						<Table.Header>
							<Table.Row>
								<Table.Head className='w-12'>
									<Checkbox
										checked={isAllSelected}
										onCheckedChange={toggleSelectAll}
									/>
								</Table.Head>
								<Table.Head
									className='font-montserrat-medium cursor-pointer hover:bg-gray-50'
									onClick={() => handleSort('name')}>
									Name {getSortIcon('name')}
								</Table.Head>
								<Table.Head
									className='font-montserrat-medium cursor-pointer hover:bg-gray-50'
									onClick={() => handleSort('category')}>
									Category {getSortIcon('category')}
								</Table.Head>
								<Table.Head
									className='font-montserrat-medium cursor-pointer hover:bg-gray-50'
									onClick={() => handleSort('date')}>
									Date {getSortIcon('date')}
								</Table.Head>
								<Table.Head className='font-montserrat-medium'>Notes</Table.Head>
								<Table.Head
									className='font-montserrat-medium text-right cursor-pointer hover:bg-gray-50'
									onClick={() => handleSort('amount')}>
									Amount {getSortIcon('amount')}
								</Table.Head>
								<Table.Head className='font-montserrat-medium text-center'>
									Actions
								</Table.Head>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{expenses.length > 0 ? (
								expenses.map((expense) => (
									<Table.Row key={expense.id} className='hover:bg-gray-50'>
										<Table.Cell>
											<Checkbox
												checked={selectedIds.includes(expense.id)}
												onCheckedChange={() => toggleSelection(expense.id)}
											/>
										</Table.Cell>
										<Table.Cell className='font-montserrat max-w-[200px]'>
											<EditableCell
												value={expense.name}
												field='name'
												onSave={(value) =>
													handleUpdateField(expense.id, 'name', value)
												}
											/>
										</Table.Cell>
										<Table.Cell className='font-montserrat max-w-[150px]'>
											<EditableCell
												value={expense.category}
												field='category'
												type='select'
												options={[
													'Social Media Ads',
													'Google Ads',
													'Photography',
													'Video Production',
													'Graphic Design',
													'Print Materials',
													'Website Maintenance',
													'Staging',
													'Open House Supplies',
													'Other Ads',
													'Hard Mailers',
													'Other',
												]}
												onSave={(value) =>
													handleUpdateField(expense.id, 'category', value)
												}
											/>
										</Table.Cell>
										<Table.Cell className='font-montserrat max-w-[120px]'>
											<EditableCell
												value={expense.date}
												field='date'
												type='date'
												onSave={(value) =>
													handleUpdateField(expense.id, 'date', value)
												}
											/>
										</Table.Cell>
										<Table.Cell className='font-montserrat text-gray-600 max-w-[250px]'>
											<EditableCell
												value={expense.notes}
												field='notes'
												onSave={(value) =>
													handleUpdateField(expense.id, 'notes', value)
												}
											/>
										</Table.Cell>
										<Table.Cell className='font-montserrat-medium text-right'>
											<EditableCell
												value={expense.amount}
												field='amount'
												type='currency'
												onSave={(value) =>
													handleUpdateField(expense.id, 'amount', value)
												}
											/>
										</Table.Cell>
										<Table.Cell className='text-center'>
											<Button
												onClick={() => {
													if (
														window.confirm(
															'Are you sure you want to delete this expense?'
														)
													) {
														deleteExpense(expense.id);
													}
												}}
												variant='ghost'
												size='sm'
												className='text-red-500 hover:text-red-700'>
												<Trash2 className='w-4 h-4' />
											</Button>
										</Table.Cell>
									</Table.Row>
								))
							) : (
								<Table.Row>
									<Table.Cell
										colSpan={7}
										className='text-center py-8 text-gray-500 font-montserrat'>
										No expenses found for this month
									</Table.Cell>
								</Table.Row>
							)}
						</Table.Body>
					</Table>
					</div>

					{/* Running Total */}
					{expenses.length > 0 && (
						<div className='border-t border-gray-200 p-4 bg-gray-50'>
							<div className='flex justify-end'>
								<div className='text-right'>
									<p className='text-sm font-montserrat text-gray-600'>
										Total ({filteredExpenses.length} expenses)
									</p>
									<p className='text-2xl font-glacial font-bold text-clientBlack'>
										{formatCurrency(total)}
									</p>
								</div>
							</div>
						</div>
					)}

					{/* Pagination */}
					{totalPages > 1 && (
						<div className='border-t border-gray-200 p-4 flex items-center justify-between'>
							<p className='text-sm font-montserrat text-gray-600'>
								Page {currentPage} of {totalPages}
							</p>
							<div className='flex gap-2'>
								<Button
									onClick={() => setCurrentPage(currentPage - 1)}
									disabled={currentPage === 1}
									variant='outline'
									size='sm'>
									Previous
								</Button>
								<Button
									onClick={() => setCurrentPage(currentPage + 1)}
									disabled={currentPage === totalPages}
									variant='outline'
									size='sm'>
									Next
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Expense Form Modal */}
			<ExpenseForm
				isOpen={isFormOpen}
				onClose={() => setIsFormOpen(false)}
				onSubmit={handleFormSubmit}
				initialData={editingExpense}
			/>
		</div>
	);
};

export default ClientExpenseTable;
